import { isVerificationRequired, notifySessionExpired } from "./session-events";

export interface SendMessageModel {
  chatId: string;
  message: string;
  date: string;
  imageData?: File[];
  reply?: string;
  houseId?: string;
  replyId?: string;
  syncId?: string;
}

const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Builds the multipart body for /api/chat/send-message (audit W01).
 * Each image is appended exactly once; empty optional fields are omitted
 * instead of being sent as the strings "undefined" / "null".
 */
export function buildMessageFormData(body: SendMessageModel): FormData {
  const formData = new FormData();
  for (const [key, value] of Object.entries(body)) {
    if (key === "imageData") continue;
    if (value === undefined || value === null || value === "") continue;
    formData.append(key, String(value));
  }
  for (const file of body.imageData ?? []) {
    formData.append("images", file);
  }
  return formData;
}

async function request(url: string, init: RequestInit): Promise<ApiResponse> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, { ...init, signal: controller.signal });
    const body = await res.json();
    if (res.status === 401 && !isVerificationRequired(body?.message)) {
      // Previously `signOutStart()` was called without being dispatched,
      // so an expired session was never cleaned up.
      notifySessionExpired();
    }
    return body;
  } catch (error) {
    return ERROR(
      (error as Error)?.name === "AbortError"
        ? "The request timed out. Please check your connection and try again."
        : error
    );
  } finally {
    clearTimeout(timer);
  }
}

class API {
  private headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  async get(url: string, token: string): Promise<ApiResponse> {
    return request(url, {
      method: "GET",
      headers: { Authorization: token ?? "", ...this.headers },
    });
  }

  async post({
    url,
    body,
    token,
  }: {
    url: string;
    token?: string;
    body: any;
  }): Promise<ApiResponse> {
    return request(url, {
      method: "POST",
      headers: { Authorization: token ?? "", ...this.headers },
      body: JSON.stringify(body),
    });
  }

  async delete({
    url,
    body,
    token,
  }: {
    url: string;
    token?: string;
    body?: any;
  }): Promise<ApiResponse> {
    return request(url, {
      method: "DELETE",
      headers: { Authorization: token ?? "", ...this.headers },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  /** Sends a voice note to /api/chat/audio-upload (field `audio`). */
  async postAudio({
    url,
    token,
    audio,
    fields,
  }: {
    url: string;
    token: string;
    audio: File;
    fields: Omit<SendMessageModel, "imageData" | "message" | "houseId">;
  }): Promise<ApiResponse> {
    const formData = buildMessageFormData({ ...fields, message: "" });
    formData.append("audio", audio);
    return request(url, {
      method: "POST",
      headers: { Authorization: token },
      body: formData,
    });
  }

  async postFormData({
    url,
    body,
    token,
  }: {
    url: string;
    token: string;
    body: SendMessageModel;
  }): Promise<ApiResponse> {
    return request(url, {
      method: "POST",
      headers: { Authorization: token },
      body: buildMessageFormData(body),
    });
  }
}

const ERROR = (error: any): ApiResponse => ({
  success: false,
  statusCode: 500,
  message: error?.toString() ?? "Something went wrong. Try again later",
});

export interface ApiResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: any;
}

const ApiClient = new API();

export default ApiClient;
